import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useScrolledToEnd } from '../../hooks/useScrolledToEnd';
import { useProgress } from '../hooks/useProgress';
import { useWeddingDay } from '../state/useWeddingDay';
import { customRituals, isOutsideWindow } from '../state/derive';
import { fmtDateLong, fmtTime } from '../state/format';
import { fetchSubmissionPreview } from '../state/api';
import { getBriefIds } from '../state/session';
import { TickBox } from './controls';
import { RITUAL } from '../../constants';

const e = React.createElement;

/**
 * The acknowledgement gate. Everything it lists is a request: a custom ritual
 * and a timing outside the package hours both go to the coordinator, so
 * neither may read as confirmed here.
 */
const SubmitGate = ({ open, onClose, onSubmit }) => {
  const { submit, locked, sharedLocked, events, windows, packageSpan, goTo, sections } =
    useWeddingDay();
  const { allComplete, incompleteLabels } = useProgress();
  // What the server would actually file. Read from it rather than worked
  // out here, so a form reopened for one correction lists that correction
  // and not everything the couple has ever answered.
  const [asked, setAsked] = useState(null);
  // Told apart from still loading: with no list there is nothing to say
  // that the schedule is clean, only that it could not be checked.
  const [previewFailed, setPreviewFailed] = useState(false);
  // What the pane holds decides how far there is to scroll, and the list
  // only arrives after the gate opens.
  const { ref, reachedEnd, onScroll } = useScrolledToEnd(
    open,
    `${allComplete}:${previewFailed}:${asked ? asked.length : 'loading'}`
  );

  // What submitting actually means, in the order it matters: what the list
  // above is, what it cannot move, what closes, and who else it closes for.
  const terms = [
    {
      title: 'These are requests, not confirmations',
      body: 'Everything listed above is a request, not a confirmation. '
        + 'Added rituals and out-of-hours timings are subject to internal '
        + 'review, team availability and coordinator approval. Your '
        + 'coordinator will confirm the final status of each before the '
        + 'wedding day.',
    },
    {
      title: 'Your package stays as booked',
      body: 'Package events, dates and cities remain as booked unless '
        + 'separately approved.',
    },
    {
      title: 'Submitting closes the form for both of you',
      body: 'Once you submit, the form is locked, and neither of you can '
        + 'edit your timeline, family details or day notes. The only thing '
        + 'that stays open is any surprise you’ve added yourself. If '
        + 'something needs to change after that, please get in touch with '
        + 'your coordinator.',
    },
    {
      title: sharedLocked
        ? 'Your partner has already submitted'
        : 'It closes for your partner too',
      body: sharedLocked
        ? 'Your partner has already submitted, so the timeline, family and '
          + 'special moments are closed. You are submitting your own part '
          + 'of the form.'
        : 'You submit your own part. Submitting closes the timeline, family '
          + 'and special moments for both of you, and your partner can still '
          + 'write and submit their own Day Notes.',
    },
    {
      warn: true,
      title: 'Talk to your partner first',
      body: 'You are filling this form for both of you. Once it is in, it '
        + 'locks for your partner as well, and only your own surprises stay '
        + 'open. Please talk to them before you submit.',
    },
  ];
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // A tick given before the list arrived is not a tick on the list. When
  // the pane grows and the end has to be reached again, the tick goes with
  // it, since the button is shut by then and could not be unticked.
  useEffect(() => {
    if (!reachedEnd) setAck(false);
  }, [reachedEnd]);
  // The terms open in the pane, right under "All sections complete", and
  // are brought into view there. The footer keeps only the tick and the
  // buttons, so on a phone they are never pushed off the bottom.
  const termsRef = useRef(null);
  useEffect(() => {
    const node = termsRef.current;
    if (ack && node && node.scrollIntoView) {
      node.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }, [ack]);

  useEffect(() => {
    if (!open) return;
    // Each opening is read afresh. A tick from the last one would skip the
    // scroll to the end, and its list is of a form that may have moved.
    setAck(false);
    setError(null);
    setAsked(null);
    setPreviewFailed(false);
    const { bookingId, briefUuid } = getBriefIds() || {};
    if (!bookingId || !briefUuid) return;
    let live = true;
    fetchSubmissionPreview({ bookingId, briefUuid })
      .then((res) => live && setAsked(res.changes || []))
      // The gate still submits without it; the list is what is lost.
      .catch(() => live && setPreviewFailed(true));
    return () => { live = false; };
  }, [open]);

  if (!open || locked) return null;

  const rituals = customRituals(events);
  // The package's own events, split by whether they run outside the hours the
  // booking pays for. Every event the couple has touched is pending before
  // they submit, so being pending says nothing about which side of the line
  // it falls on. The rituals are listed on their own below.
  const timed = (x) => x.kind !== RITUAL && x.start && x.end;
  const outside = events.filter((x) => timed(x) && isOutsideWindow(x, windows));
  const inside = events.filter((x) => timed(x) && !isOutsideWindow(x, windows));
  // What the package covers, said before anything is measured against it: it
  // opens on the first date and closes on the last, so those two readings are
  // the booking's hours rather than one line per date.
  const span = (w) =>
    `${fmtTime(w.start)} to ${fmtTime(w.end)}${w.end_next_day ? ' next day' : ''}`;
  // Said either way. A date the package never recorded reads as not set rather
  // than going missing, so the couple can see which end is still open.
  const hours = packageSpan || {};
  const spanRow = (label, name, date, time, tail) =>
    e('li', { key: label }, [
      e('span', { className: 'wd-gate-when', key: 'w' },
        name ? `${label} · ${name}` : label),
      e('span', { className: 'wd-gate-time', key: 't' },
        time ? `${fmtDateLong(date)} · ${fmtTime(time)}${tail}` : 'Not set'),
    ]);
  // One row per event: what it is, the times they set, and the hours booked
  // under it where that date recorded any.
  const eventRow = (r) => {
    const w = windows[r.date] || null;
    return e('li', { key: String(r.id) }, [
      e('span', { className: 'wd-gate-when', key: 'w' },
        `${r.name} · ${fmtDateLong(r.date)}`),
      e('span', { className: 'wd-gate-time', key: 't' },
        r.start && r.end
          ? `${fmtTime(r.start)} to ${fmtTime(r.end)}`
          : 'timings not set'),
      w
        ? e('span', { className: 'wd-gate-note', key: 'b' }, `Booked ${span(w)}`)
        : null,
    ]);
  };
  // The server names incomplete sections by title; the flow navigates by key.
  const sectionFor = (title) => sections.find((s) => s.title === title)?.key;
  const sectionTitle = (key) => sections.find((s) => s.key === key)?.title || '';
  // Left out: the timings and the rituals are spelled out in the sections
  // above, and what the couple planned is theirs to arrange rather than
  // something they are being asked to confirm here.
  const SKIP = ['out_of_hours', 'added_ritual', 'activity'];
  const rest = (asked || []).filter((row) => {
    if (SKIP.includes(row.field)) return false;
    // A shot on an event with no timings yet has nothing to confirm. It
    // still goes to the coordinator; it is just not worth a line here
    // saying only that it is undecided.
    return !(row.field === 'couple_shot_plan' && !row.start);
  });
  // The clock as the rest of this screen reads it, where the row carries
  // one: a shot is a half hour, not a moment.
  const askedWhen = (row) => {
    const when = row.start
      ? `${fmtTime(row.start)}${row.end ? ` to ${fmtTime(row.end)}` : ''}`
      : '';
    const what = row.detail || sectionTitle(row.section);
    if (!when) return what || 'no time set';
    return what ? `${what} · ${when}` : when;
  };

  // Hung off the body rather than left in the form's tree: a transformed
  // ancestor would otherwise be what the scrim's fixed position resolves
  // against, which cropped the modal against the top of the page.
  return createPortal(e(
    'div',
    { className: 'wd-portal' },
    e(
    'div',
    { className: 'wd-scrim', onClick: onClose },
    e('div', { className: 'wd-modal', onClick: (ev) => ev.stopPropagation() }, [
      e('header', { key: 'head' }, e('h3', null, 'Confirm what needs approval')),
      e('div', { className: 'wd-scrollpane', key: 'body', ref, onScroll }, [
        // Nothing is listed while a section is still open. What goes to
        // the coordinator is read off answers that are not all in yet, so
        // showing it now is showing a list that is going to change. The
        // one line saying what is left is the whole modal until then.
        allComplete
        ? e('section', { key: 'hours', className: 'wd-gate-sec' }, [
          e('h4', { key: 'h' }, 'Your package hours'),
          e('ul', { key: 'l' }, [
            spanRow('Starts', hours.start_event, hours.start_date,
                    hours.start, ''),
            spanRow('Ends', hours.end_event, hours.end_date, hours.end,
                    hours.end_next_day ? ' next day' : ''),
          ]),
        ])
        : null,
        allComplete && inside.length
          ? e('section', { key: 'inside', className: 'wd-gate-sec' }, [
              e('h4', { key: 'h' }, 'Package events within those hours'),
              e('ul', { key: 'l' }, inside.map(eventRow)),
            ])
          : null,
        allComplete && outside.length
          ? e('section', { key: 'requests', className: 'wd-gate-sec' }, [
              e('h4', { key: 'h' }, 'Package events outside those hours'),
              e('ul', { key: 'l' }, outside.map(eventRow)),
            ])
          : null,
        allComplete && rituals.length
          ? e('section', { key: 'rituals', className: 'wd-gate-sec' }, [
              e('h4', { key: 'h' }, 'Rituals you added'),
              e(
                'ul',
                { key: 'l' },
                rituals.map((r) =>
                  e('li', { key: String(r.id) }, [
                    e('span', { className: 'wd-gate-when', key: 'w' },
                      `${r.name || 'Unnamed ritual'} · ${fmtDateLong(r.date)}`),
                    e('span', { className: 'wd-gate-time', key: 't' },
                      r.start && r.end
                        ? `${fmtTime(r.start)} to ${fmtTime(r.end)}`
                        : 'timings not set'),
                  ])
                )
              ),
            ])
          : null,
        // Everything else the server would file: the couple shots, the
        // sittings, the contacts, the faces and the moments. The sections
        // above already spell out the timings and the rituals, so those
        // are left out rather than said twice.
        allComplete && rest.length
          ? e('section', { key: 'asked', className: 'wd-gate-sec' }, [
              e('h4', { key: 'h' }, 'Also going to your coordinator'),
              e('ul', { key: 'l' }, rest.map((row, i) =>
                e('li', { key: String(i) }, [
                  e('span', { className: 'wd-gate-when', key: 'w' },
                    row.title),
                  e('span', { className: 'wd-gate-time', key: 't' },
                    askedWhen(row)),
                ])
              )),
            ])
          : null,
        allComplete && !rituals.length && !outside.length && !inside.length
            && !rest.length
          ? e(
              'p',
              { key: 'clean' },
              asked
                ? 'Nothing has changed since your coordinator last looked at this.'
                : previewFailed
                  ? 'We could not check what goes to your coordinator. You can still submit, and they will see everything you send.'
                  : 'Checking what goes to your coordinator...'
            )
          : null,
        e(
          'p',
          { key: 'open', className: allComplete ? 'wd-scroll-hint is-ok' : 'wd-scroll-hint' },
          allComplete
            ? 'All sections complete.'
            : `Still open: ${incompleteLabels.join(', ')}. The form goes in only once these are answered.`
        ),
        // Held back until they tick. Four walls of small print above the
        // button is what people scroll past; the same words asked for once
        // they have said they are ready get read. Each one is its own
        // point rather than a paragraph among four others.
        allComplete && ack
          ? e('div', { className: 'wd-terms', key: 'terms', ref: termsRef },
              terms.map((row, i) =>
                e('div', { className: `wd-term${row.warn ? ' is-warn' : ''}`,
                           key: String(i) }, [
                  e('h5', { className: 'wd-term-head', key: 'h' }, row.title),
                  e('p', { className: 'wd-term-body', key: 'b' }, row.body),
                ])
              ))
          : null,
      ]),
      error
        ? e('div', { className: 'wd-submiterror', key: 'err' }, [
            e('p', { key: 'm' }, error.message),
            error.pendingSections && error.pendingSections.length && sectionFor(error.pendingSections[0])
              ? e(
                  'button',
                  {
                    key: 'go',
                    type: 'button',
                    className: 'wd-btn wd-btn-ghost',
                    onClick: () => {
                      goTo(sectionFor(error.pendingSections[0]));
                      onClose();
                    },
                  },
                  `Go to ${error.pendingSections[0]}`
                )
              : null,
          ])
        : null,
      e('footer', { key: 'foot' }, [
        // Asked for only once there is something settled to acknowledge.
        allComplete
        ? e(
          'button',
          {
            key: 'ack',
            type: 'button',
            className: `wd-ackrow${ack ? ' is-on' : ''}`,
            'aria-disabled': String(!reachedEnd),
            disabled: !reachedEnd,
            onClick: () => setAck((v) => !v),
          },
          [
            e(TickBox, { checked: ack, key: 't' }),
            e(
              'span',
              { key: 'l' },
              reachedEnd ? 'I acknowledge these items need approval' : 'Scroll to the end to continue'
            ),
          ]
        )
        : null,
        e('div', { className: 'wd-acts', key: 'acts' }, [
          e(
            'button',
            { key: 'back', type: 'button', className: 'wd-btn wd-btn-ghost', onClick: onClose },
            'Back'
          ),
          e(
            'button',
            {
              key: 'go',
              type: 'button',
              className: 'wd-btn wd-btn-brand',
              // Day Notes aside, every section is answered before the form
              // goes in, and the server refuses it either way.
              disabled: !ack || !reachedEnd || busy || !allComplete,
              onClick: () => {
                setBusy(true);
                setError(null);
                // Every failure here is permanent, so there is no retry.
                Promise.resolve(submit(ack === true))
                  .then(onSubmit)
                  .catch((err) => setError(err))
                  .finally(() => setBusy(false));
              },
            },
            'Submit for approval'
          ),
        ]),
      ]),
    ])
  )), document.body);
};

export default SubmitGate;
